import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock, ShieldCheck, Sparkles, BarChart3, Receipt, Globe, Share2, CreditCard, Users } from "lucide-react";
import SEO from "../components/SEO";
import AppSlider from "../components/AppSlider";

export default function Home() {
    const scrollToFeatures = () => {
        document.getElementById("products-section")?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <div className="w-full overflow-hidden relative">
            <SEO
                title="blinqbooks — Best AI Business Software | Accounting, POS, Payroll & Website Builder"
                description="blinqbooks is the best all-in-one AI business platform in 2026. Cloud accounting software, POS system, payroll, website builder, expense tracker & social media management. Free trial — no credit card required."
            />

            {/* Ambient Background Glow for Premium Feel */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[1000px] h-[600px] bg-gradient-to-b from-blue-100/40 via-cyan-50/15 to-transparent dark:from-blue-950/20 dark:via-cyan-950/10 blur-3xl opacity-85 pointer-events-none -z-10 rounded-b-full"></div>



            {/* Hero Section */}
            <section className="min-h-[90vh] flex flex-col md:flex-row items-center justify-center gap-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-16 md:py-28 relative overflow-visible z-10">
                {/* Text Content */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="flex-1 text-center md:text-left z-10 pt-10 md:pt-0"
                >
                    <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-white/80 dark:bg-slate-900/60 border border-blue-150/40 dark:border-blue-900/40 shadow-sm text-[#0F4C81] dark:text-[#38BDF8] text-sm font-semibold tracking-wide uppercase">
                        <Sparkles className="w-4 h-4 text-brand-tech animate-pulse" />
                        Made for Modern Accounting
                    </div>
                    <h1 className="text-5xl md:text-7xl font-extrabold mb-6 tracking-tight leading-[1.1] text-primary">
                        Run Your Entire <br className="hidden md:block" />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0F4C81] via-[#06B6D4] to-[#16A34A] dark:from-[#38BDF8] dark:via-[#22D3EE] dark:to-[#34D399] drop-shadow-sm">
                            Business on AI
                        </span>
                    </h1>
                    <p className="text-lg md:text-xl mb-10 text-secondary leading-relaxed max-w-xl mx-auto md:mx-0 font-medium opacity-90">
                        The All-In-One suite made for modern accounting. Payroll, Websites, POS, and more—all seamlessly integrated and supercharged by AI.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4">
                        <Link to="/signup" className="w-full sm:w-auto block text-center">
                            <motion.button
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                className="w-full px-9 py-4 rounded-full font-extrabold text-lg text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500 hover:from-blue-500 hover:to-emerald-400 shadow-xl shadow-blue-600/25 transition-all duration-300 cursor-pointer"
                            >
                                Start Free Trial ✨
                            </motion.button>
                        </Link>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={scrollToFeatures}
                            className="btn-secondary w-full sm:w-auto px-8 py-4 text-lg bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm cursor-pointer"
                        >
                            Explore Apps
                        </motion.button>
                    </div>
                    <div className="mt-8 flex items-center justify-center md:justify-start gap-4 text-sm text-secondary font-medium">
                        <div className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-green-500" /> No credit card required</div>
                        <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></span>
                        <div className="flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-amber-500" /> Free 14-day trial</div>
                    </div>
                </motion.div>

                {/* Hero Image / Graphic */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="flex-1 w-full max-w-xl relative mt-10 md:mt-0"
                >
                    <div className="absolute -inset-10 bg-gradient-to-tr from-[#0F4C81]/15 via-[#06B6D4]/15 to-[#16A34A]/10 rounded-[3rem] blur-3xl dark:opacity-30"></div>

                    {/* Floating Dashboard Element Representation */}
                    <div className="relative rounded-[2.5rem] p-6 md:p-8 grid grid-cols-2 lg:grid-cols-3 gap-4 bg-slate-50/50 dark:bg-slate-900/30 backdrop-blur-xl shadow-[0_30px_60px_-20px_rgba(15,76,129,0.08)] dark:shadow-none animate-float">
                        <div className="col-span-2 lg:col-span-3 flex items-center justify-between mb-2 pb-4">
                            <div className="flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-400/90 shadow-sm"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-400/90 shadow-sm"></div>
                                <div className="w-3 h-3 rounded-full bg-green-400/90 shadow-sm"></div>
                            </div>
                            <div className="text-xs text-secondary font-medium px-4 py-1 bg-slate-100/50 dark:bg-slate-800/50 rounded-full tracking-wider">dashboard.blinqbooks.com</div>
                        </div>

                        {/* Mock App Cards */}
                        {[
                            { icon: BarChart3, bg: "hover:bg-blue-50/50 dark:hover:bg-blue-950/20", iconBg: "bg-blue-100/50 dark:bg-blue-500/20", color: "text-blue-600 dark:text-blue-400" },
                            { icon: Receipt, bg: "hover:bg-green-50/50 dark:hover:bg-green-950/20", iconBg: "bg-green-100/50 dark:bg-green-500/20", color: "text-green-600 dark:text-green-400" },
                            { icon: Globe, bg: "hover:bg-cyan-50/50 dark:hover:bg-cyan-950/20", iconBg: "bg-cyan-100/50 dark:bg-cyan-500/20", color: "text-cyan-605 dark:text-cyan-400" },
                            { icon: Share2, bg: "hover:bg-sky-50/50 dark:hover:bg-sky-950/20", iconBg: "bg-sky-100/50 dark:bg-sky-500/20", color: "text-sky-600 dark:text-sky-400" },
                            { icon: CreditCard, bg: "hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20", iconBg: "bg-emerald-100/50 dark:bg-emerald-500/20", color: "text-emerald-600 dark:text-emerald-400" },
                            { icon: Users, bg: "hover:bg-teal-50/50 dark:hover:bg-teal-950/20", iconBg: "bg-teal-100/50 dark:bg-teal-500/20", color: "text-teal-600 dark:text-teal-400" }
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 + (index * 0.1) }}
                                className={`p-5 rounded-3xl text-center flex flex-col items-center ${item.bg} transition-all duration-300 cursor-pointer`}
                            >
                                <div className={`w-12 h-12 ${item.iconBg} rounded-2xl flex items-center justify-center mb-3`}>
                                    <item.icon className={`w-6 h-6 ${item.color}`} />
                                </div>
                                <div className="h-2 w-16 bg-slate-200 dark:bg-slate-800 rounded-full"></div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </section>

            {/* Interactive Feature Slider Component */}
            <section id="products-section" className="bg-surface relative border-y border-slate-200/60 dark:border-slate-800/60">
                <AppSlider
                    title="All Important Apps on One Slider"
                    subtitle="Explore all 9 core business modules. Swipe, click, or test them on mobile."
                />
            </section>

            {/* Security & AI Section */}
            <section className="py-24 md:py-32 bg-base relative overflow-hidden">
                {/* Ambient background glow */}
                <div className="absolute right-[-10%] top-[20%] w-[500px] h-[500px] bg-gradient-to-b from-[#06B6D4]/8 to-transparent dark:from-[#22D3EE]/5 blur-[120px] pointer-events-none rounded-full -z-10"></div>
                <div className="absolute left-[-10%] bottom-[10%] w-[500px] h-[500px] bg-gradient-to-t from-[#0F4C81]/8 to-transparent dark:from-[#38BDF8]/5 blur-[120px] pointer-events-none rounded-full -z-10"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className="inline-block px-3.5 py-1.5 mb-3 rounded-full bg-blue-50 dark:bg-blue-955/40 text-[#0F4C81] dark:text-[#38BDF8] text-xs font-bold uppercase tracking-wider">
                                Military-Grade Protection
                            </div>
                            <h2 className="text-3xl md:text-5xl font-extrabold mb-4 text-primary leading-tight">
                                Bank-Grade Security <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0F4C81] to-[#06B6D4] dark:from-[#38BDF8] dark:to-[#22D3EE]">Powered by AI</span>
                            </h2>
                            <div className="space-y-4 mt-6">
                                <div className="flex items-start gap-4 group cursor-default">
                                    <div className="p-3 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 text-[#0F4C81] dark:text-[#38BDF8] shadow-sm transition-transform group-hover:scale-105 rounded-xl shrink-0">
                                        <Shield className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg text-primary mb-0.5">Enterprise Encryption</h3>
                                        <p className="text-sm text-secondary leading-relaxed">Your data is secured with 256-bit AES encryption and regular automated backups across distributed servers.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 group cursor-default">
                                    <div className="p-3 bg-gradient-to-br from-green-50 to-cyan-50 dark:from-green-950/20 dark:to-cyan-950/20 text-green-600 dark:text-green-455 shadow-sm transition-transform group-hover:scale-105 rounded-xl shrink-0">
                                        <Lock className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-lg text-primary mb-0.5">AI Fraud Detection</h3>
                                        <p className="text-sm text-secondary leading-relaxed">Real-time monitoring algorithms constantly learn and adapt to detect and prevent suspicious activity instantly.</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.5, delay: 0.15 }}
                            className="relative w-full max-w-sm sm:max-w-md mx-auto md:ml-auto md:mr-0"
                        >
                            {/* Background Aura */}
                            <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/15 via-cyan-500/10 to-emerald-500/10 rounded-[2.5rem] blur-xl pointer-events-none"></div>

                            {/* Compressed Main Card */}
                            <div className="relative bg-white/85 dark:bg-slate-900/85 border border-slate-200/80 dark:border-slate-800/80 p-5 sm:p-7 rounded-[2rem] backdrop-blur-xl shadow-xl overflow-hidden flex flex-col items-center text-center">
                                {/* Top Status Pill */}
                                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-[11px] font-bold uppercase tracking-wider mb-5 shadow-sm">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping"></span>
                                    24/7 Threat Protection Active
                                </div>

                                {/* Crystal-Clear Security Emblem */}
                                <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-emerald-500 p-0.5 shadow-xl shadow-blue-500/20 mb-5">
                                    <div className="w-full h-full bg-slate-900 rounded-[22px] flex items-center justify-center">
                                        <ShieldCheck className="w-10 h-10 text-emerald-400 drop-shadow" />
                                    </div>
                                </div>

                                <h4 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mb-1.5 tracking-tight">
                                    100% Encrypted & Safe
                                </h4>
                                <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 font-medium max-w-xs mb-5">
                                    Continuous zero-trust architecture protecting every transaction.
                                </p>

                                {/* Compact Security Badges */}
                                <div className="grid grid-cols-2 gap-2.5 w-full pt-3.5 border-t border-slate-100 dark:border-slate-800">
                                    <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-slate-800 text-left">
                                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Protocol</div>
                                        <div className="text-xs font-black text-slate-900 dark:text-white font-mono">TLS 1.3 / AES-256</div>
                                    </div>
                                    <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-slate-800 text-left">
                                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Compliance</div>
                                        <div className="text-xs font-black text-emerald-600 dark:text-emerald-400 font-mono">SOC2 & ISO 27001</div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Hire Developer Section */}
            <section className="py-24 md:py-32 bg-surface relative overflow-hidden text-center md:text-left">
                {/* Ambient background glow */}
                <div className="absolute left-1/3 top-1/4 w-[600px] h-[600px] bg-gradient-to-tr from-[#16A34A]/8 via-[#06B6D4]/5 to-transparent dark:from-[#34D399]/4 dark:via-transparent blur-[140px] pointer-events-none rounded-full"></div>

                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-3/4 h-[800px] bg-gradient-to-bl from-blue-50 to-transparent dark:from-blue-900/10 pointer-events-none -z-10 rounded-bl-[100%]"></div>
                <div className="absolute border-t border-slate-100 dark:border-slate-800 w-full top-0 left-0"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex flex-col md:flex-row items-center justify-between gap-16">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.5 }}
                        className="flex-1"
                    >
                        <div className="inline-block px-4 py-2 mb-6 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 text-sm font-medium shadow-sm">
                            Build Scalable Systems
                        </div>
                        <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-primary leading-[1.15]">
                            Need Custom ERP? <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0F4C81] to-[#16A34A] dark:from-[#38BDF8] dark:to-[#34D399]">
                                Hire Top 1% Developers
                            </span>
                        </h2>
                        <p className="text-secondary text-lg mb-10 max-w-xl mx-auto md:mx-0 font-medium">
                            Don't settle for off-the-shelf. Hire our expert engineering team to build scalable, secure custom software tailored strictly for your enterprise scale.
                        </p>
                        {/* Animated Tech Stack Marquee */}
                        <div className="relative flex overflow-x-hidden w-full max-w-md mx-auto md:mx-0 mb-10 [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
                            <motion.div
                                className="flex whitespace-nowrap gap-3 py-2"
                                animate={{
                                    x: ["0%", "-50%"],
                                }}
                                transition={{
                                    x: {
                                        repeat: Infinity,
                                        repeatType: "loop",
                                        duration: 15,
                                        ease: "linear",
                                    },
                                }}
                            >
                                {[
                                    "Next.js", "React", "Node.js", "Python", "AWS", "Docker", "AI/ML", "GraphQL", "Java",
                                    "Next.js", "React", "Node.js", "Python", "AWS", "Docker", "AI/ML", "GraphQL", "Java"
                                ].map((tech, i) => (
                                    <span key={i} className="px-4 py-2 rounded-full bg-white dark:bg-slate-800 text-primary text-sm font-semibold shadow-sm cursor-default flex-shrink-0">
                                        {tech}
                                    </span>
                                ))}
                            </motion.div>
                        </div>
                        <Link to="/contact" className="w-full sm:w-auto block text-center">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="btn-primary w-full sm:w-auto px-8 py-4 text-base sm:text-lg shadow-lg"
                            >
                                Get A Team Extension
                            </motion.button>
                        </Link>
                    </motion.div>

                    {/* Visual: Code/Dev Graphic */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                        className="flex-1 w-full max-w-lg relative mt-10 md:mt-0"
                    >
                        <div className="absolute -inset-6 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 rounded-3xl opacity-50 blur-3xl dark:opacity-30"></div>
                        <div className="bg-slate-900 rounded-[2rem] p-5 sm:p-6 shadow-2xl font-mono text-[12px] sm:text-xs md:text-sm leading-relaxed overflow-x-auto text-slate-300 relative border border-slate-800">
                            {/* Window Header */}
                            <div className="flex items-center gap-2 mb-6 pb-3 border-b border-slate-800/60">
                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            </div>

                            <div className="text-pink-400 inline">import</div> <div className="text-blue-300 inline">Scalability</div> <div className="text-pink-400 inline">from</div> <div className="text-emerald-300 inline">'@blinq/core'</div>;
                            <br /><br />
                            <div className="text-purple-400 inline">const</div> <div className="text-amber-300 inline">EnterpriseSystem</div> = <div className="text-cyan-400 inline">async</div> () ={">"} {"{"}
                            <div className="pl-4 text-slate-500">{"// Custom logic for your business scale"}</div>
                            <div className="pl-4"><div className="text-cyan-400 inline">await</div> deploy({"{"}</div>
                            <div className="pl-8 text-blue-200">security: <div className="text-emerald-300 inline">'Bank-Grade'</div>,</div>
                            <div className="pl-8 text-blue-200">uptime: <div className="text-emerald-300 inline">'99.99%'</div>,</div>
                            <div className="pl-8 text-blue-200">engine: <div className="text-emerald-300 inline">'AI-Powered'</div></div>
                            <div className="pl-4 text-slate-300">{"}"});</div>
                            <div className="text-slate-300">{"}"}</div>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div >
    );
}
